import React from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  TextInput,
  Image,
} from 'react-native-web';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import { Box, Inline } from '@styleseat/ui';
import { colors } from '@styleseat/brand';
import {
  BodyText,
  Button,
  HeaderText,
  RadioButton,
  Stack,
} from '../../../ui';

import RatingStars from '../../../RatingStars';
import downArrow from '../../../../../images/icons/icon-chevron-down-arrow.svg';
import upArrow from '../../../../../images/icons/icon-chevron-up-arrow.svg';
import ssPropTypes from '../../../../modules/ssPropTypes';

import './SentimentSurvey.module.scss';

const SCORES = [
  { value: '1' },
  { value: '2' },
  { value: '3' },
  { value: '4' },
  { value: '5' },
];

export const SentimentSurvey = ({
  useStarRatings,
  useScoreRatings,
  questionOne,
  primaryInputQuestion,
  placeholder,
  handleScoreSelected,
  handleStarSelected,
  onChangeText,
  onSubmit,
  onMinBtnPress,
  selectedScore,
  ratingStars,
  surveyComplete,
  surveyMinimized,
  adjustForNavBar,
  multiChoiceOptions,
  handleMultiChoiceSelected,
  selectedChoice,
  canSubmit,
}) => {
  const minButtonStyle = surveyMinimized ? 'collapsed' : 'survey-min-button';
  // NOTE: stylings for this component will need eventually converted to use our new ui components.
  return (
    <View styleName={classNames(
      'sentiment-survey-wrapper',
      {
        'sentiment-survey-wrapper--minimized': surveyMinimized,
        'sentiment-survey-wrapper--completed': surveyComplete,
        'sentiment-survey-wrapper--adjust-for-nav-bar': adjustForNavBar,
        'sentiment-survey-wrapper--adjust-for-nav-bar--primary-input-question': primaryInputQuestion && adjustForNavBar,
        'sentiment-survey-wrapper--adjust-for-nav-bar--multiple-choice-question--3': multiChoiceOptions && multiChoiceOptions.length === 3 && adjustForNavBar,
        'sentiment-survey-wrapper--adjust-for-nav-bar--multiple-choice-question--3--can-submit': multiChoiceOptions && multiChoiceOptions.length === 3 && adjustForNavBar && canSubmit,
        'sentiment-survey-wrapper--adjust-for-nav-bar--complete': surveyComplete && adjustForNavBar,
        'sentiment-survey-wrapper--minimized--adjust-for-nav-bar': surveyMinimized && adjustForNavBar,
      },
    )}
    >
      <View
        styleName={classNames(
          'btn-box-shadow',
          {
            'btn-box-shadow--adjust-for-nav-bar': adjustForNavBar,
          },
        )}
      />
      <TouchableOpacity
        onPress={onMinBtnPress}
        activeOpacity={0.6}
        testID="sentiment-survey-minimize"
        styleName={classNames(
          minButtonStyle,
          {
            [`${minButtonStyle}--adjust-for-nav-bar`]: adjustForNavBar,
          },
        )}
      >
        {surveyMinimized && (
          <Image
            source={upArrow}
            style={{
              height: 15,
              width: 23,
            }}
          />
        )}
        {!surveyMinimized && (
          <Image
            source={downArrow}
            style={{
              height: 15,
              width: 23,
            }}
          />
        )}
      </TouchableOpacity>
      {!surveyComplete && (
        <View
          styleName={classNames(
            'sentiment-survey',
            {
              'sentiment-survey--adjust-for-nav-bar': adjustForNavBar,
            },
          )}
        >
          <View styleName="question-one-container">
            <Box paddingX={24}>
              <HeaderText
                level={3}
                textAlign="center"
              >
                {questionOne}
              </HeaderText>
            </Box>
            {useScoreRatings && (
              <React.Fragment>
                <View
                  styleName="scores-container"
                  testID="sentiment-survey-scores-container"
                >
                  {SCORES.map(score => {
                    const selected = Number(score.value) === Number(selectedScore);
                    return (
                      <TouchableOpacity
                        styleName={classNames(
                          'score',
                          { 'score--selected': selected },
                        )}
                        onPress={handleScoreSelected(score.value)}
                        key={`sentiment-score-${score.value}`}
                        testID="sentiment-score-button"
                      >
                        <Text
                          styleName={classNames(
                            'survey-text',
                            { 'score--selected__value': selected },
                          )}
                        >
                          {score.value}
                        </Text>
                      </TouchableOpacity>
                    );
                  })}
                </View>
                <View styleName="agree-text-container">
                  <Text styleName="survey-text agree-text">Strongly disagree</Text>
                  <Text styleName="survey-text agree-text">Strongly agree</Text>
                </View>
              </React.Fragment>
            )}
            {useStarRatings && (
              <Stack
                paddingTop={12}
                space={8}
                testID="sentiment-survey-stars-container"
                alignItems="center"
              >
                <RatingStars
                  size={45}
                  ratingStars={ratingStars}
                  onRatingChange={handleStarSelected}
                />
                <BodyText
                  appearance="smaller"
                  textAlign="center"
                >
                  Tap to rate
                </BodyText>
              </Stack>

            )}
            {multiChoiceOptions && (
              <Stack
                paddingTop={12}
                paddingRight={20}
                space={8}
                testID="sentiment-survey-multichoice-container"
              >
                {multiChoiceOptions.map((option, index) => (
                  <Inline
                    key={option}
                  >
                    <Box paddingTop={4}>
                      <RadioButton
                        isSelected={index === selectedChoice}
                        onToggle={() => handleMultiChoiceSelected(index)}
                        testID="sentiment-survey-multichoice-buttons"
                        justifyContent="flex-start"
                        backgroundColor={colors.whiteWash}
                        padding={12}
                      />
                    </Box>
                    <BodyText>
                      {option}
                    </BodyText>
                  </Inline>
                ))}
              </Stack>
            )}
          </View>
          <View
            styleName={classNames(
              'primary-input-question',
              { 'primary-input-question--show': primaryInputQuestion },
            )}
            testID="survey-primary-input-question-container"
          >
            <Text styleName="survey-text primary-input-question-text">{primaryInputQuestion}</Text>
            <TextInput
              styleName="survey-text primary-input-question-input"
              onChangeText={text => { onChangeText(text); }}
              placeholder={placeholder}
              style={{ backgroundColor: '#FFFFFF' }} // need to set BG color here so we don't lose color when onFocused
              multiline
              testID="sentiment-survey-primary-input"
            />
          </View>
          {canSubmit && (
            <Box
              paddingTop={28}
              paddingBottom={24}
              paddingX={84}
            >
              <Button
                size="large"
                text="Submit"
                onClick={onSubmit}
                testID="sentiment-survey-submit-button"
              />
            </Box>
          )}
        </View>
      )}
      {surveyComplete && (
        <View
          styleName={classNames(
            'sentiment-survey-complete',
            {
              'sentiment-survey-complete--adjust-for-nav-bar': adjustForNavBar,
            },
          )}
        >
          <View>
            <Text styleName="survey-text">Thank you for your response!</Text>
          </View>
        </View>
      )}
    </View>
  );
};

SentimentSurvey.propTypes = {
  /** show the star rating option */
  useStarRatings: PropTypes.bool,

  /** show the score rating option */
  useScoreRatings: PropTypes.bool,

  /** the question for the survey */
  questionOne: PropTypes.string.isRequired,

  /** the primary input question for the survey */
  primaryInputQuestion: PropTypes.string,

  /** place holder text for input box */
  placeholder: PropTypes.string.isRequired,

  /** handle selecting a score */
  handleScoreSelected: PropTypes.func,

  /** handle selecting a star */
  handleStarSelected: PropTypes.func,

  /** input text change */
  onChangeText: PropTypes.func,

  /** handle submit button */
  onSubmit: PropTypes.func,

  /** handle minimize button */
  onMinBtnPress: PropTypes.func,

  /** the score that is currently selected */
  selectedScore: ssPropTypes.numberLike,

  /** the star rating */
  ratingStars: ssPropTypes.numberLike,

  /** survey is minimized */
  surveyMinimized: PropTypes.bool,

  /** show completed state of component */
  surveyComplete: PropTypes.bool,

  /** true to adjust for bottom nav bar */
  adjustForNavBar: PropTypes.bool,

  /** the options for the multiple choice section */
  multiChoiceOptions: PropTypes.array,

  /** handles the multi choice option click */
  handleMultiChoiceSelected: PropTypes.func,

  /** the choice selected from the multi choice options */
  selectedChoice: PropTypes.number,

  /** shows the submit button */
  canSubmit: PropTypes.bool,
};

SentimentSurvey.defaultProps = {
  useStarRatings: false,
  useScoreRatings: false,
  primaryInputQuestion: '',
  handleScoreSelected: () => { },
  handleStarSelected: () => { },
  onChangeText: () => { },
  onSubmit: () => { },
  onMinBtnPress: () => { },
  selectedScore: null,
  ratingStars: null,
  surveyMinimized: false,
  surveyComplete: false,
  adjustForNavBar: false,
  multiChoiceOptions: null,
  handleMultiChoiceSelected: () => { },
  selectedChoice: null,
  canSubmit: false,
};
